/**
 * DiscoveryWorkflowsPageV2 — Phase 5 of the Rebirth migration.
 *
 * Discovery → Workflows. Lists workflows surfaced from agent shadowing
 * that haven't been registered as governed Workflows yet.
 *
 * Per Design Principles §3.3.1, the action per row is "Register" —
 * which opens the workflow registration wizard with metadata pre-populated.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workflow, Search as SearchIcon, RefreshCw, ArrowRight, FileText } from 'lucide-react';
import * as api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { isRebirthDiscoveryEnabled } from '../utils/featureFlags';
import WorkflowDiscoveryPage from './WorkflowDiscoveryPage';
import {
  Button, Pill, Table, Slideout, EmptyState, Card, Skeleton,
} from '../components/v2';

const DEMO = [
  { id: 'w1', name: 'partner-extraction-v2',  agent: 'partner-llm-7',     nodeCount: 14, lastRunAt: '2026-05-08T13:48:00Z', complexity: 'high',   status: 'unregistered' },
  { id: 'w2', name: 'pulse-classify-tier-1',  agent: 'pulse-classifier-3', nodeCount: 6,  lastRunAt: '2026-05-08T13:30:00Z', complexity: 'low',    status: 'unregistered' },
  { id: 'w3', name: 'aria-mirror-stream',     agent: 'aria-prod-replica',  nodeCount: 22, lastRunAt: '2026-05-08T13:15:00Z', complexity: 'high',   status: 'pending_review' },
  { id: 'w4', name: 'eu-pii-passthrough',     agent: 'unknown-llm-eu-west-1', nodeCount: 8, lastRunAt: '2026-05-08T11:42:00Z', complexity: 'medium', status: 'unregistered' },
];

function DiscoveryWorkflowsPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Bracket-notation soft-dependency guard — see W134 in AgentDetailPageV2.
      let res;
      const fetchList = api['listDiscoveredWorkflows'];
      const fetchFallback = api['getWorkflowDiscovery'];
      if (typeof fetchList === 'function')          res = await fetchList();
      else if (typeof fetchFallback === 'function') res = await fetchFallback();
      else res = null;
      const list = res && (Array.isArray(res) ? res : (res.items || res.data));
      setItems(list || DEMO);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Failed to load discoveries', description: err?.message || String(err) });
      setItems(DEMO);
    } finally {
      setRefreshing(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = (items || []).filter((row) =>
    !search || (
      (row.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (row.agent || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const registerOne = (row) => {
    addToast({ tone: 'info', title: 'Register workflow', description: `${row.name} — opening registration wizard` });
    navigate('/workflow-discovery', { state: { discoveredWorkflow: row } });
  };

  const complexityTone = (c) => c === 'low' ? 'success' : c === 'medium' ? 'warning' : 'danger';

  /* DEF-DISC-MARGIN-002 — Canon is 1600px (WorkflowDiscoveryPageV2).
   * This inner JSX is currently unreachable: the default export at line
   * 186 delegates to WorkflowDiscoveryPageV2. Updated for hygiene so a
   * future refactor that re-enables this path inherits the right canon. */
  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1600px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Discovery · Workflows</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {items == null ? 'Loading…' : `${filtered.length} discovered workflow(s) · not yet registered`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="bg-v2-muted border border-transparent rounded-v2-sm h-8 px-3 flex items-center gap-2 hover:border-v2-border hover:bg-v2-surface transition-colors duration-v2">
            <SearchIcon size={14} aria-hidden="true" className="text-v2-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by workflow or agent…"
              className="bg-transparent border-0 outline-none text-[12.5px] text-v2-ink placeholder:text-v2-ink-muted w-[220px]"
            />
          </div>
          <Button variant="ghost" iconLeft={<RefreshCw />} onClick={load} loading={refreshing}>Refresh</Button>
          <Button variant="primary" onClick={() => navigate('/workflow-discovery')}>Bulk register</Button>
        </div>
      </header>

      {items == null && (
        <Card><Card.Body><Skeleton variant="rect" height={240} /></Card.Body></Card>
      )}

      {items != null && filtered.length === 0 && (
        <EmptyState
          icon={<Workflow size={20} />}
          title={search ? 'No discoveries match the search' : 'No discovered workflows'}
          description={search ? 'Try a shorter query or clear the filter.' : 'The platform will surface unregistered workflows here as soon as they are observed running through monitored connectors.'}
          primaryAction={search ? <Button variant="primary" onClick={() => setSearch('')}>Clear search</Button> : null}
        />
      )}

      {items != null && filtered.length > 0 && (
        <Table
          ariaLabel="Discovered workflows"
          columns={[
            { key: 'name',       header: 'Workflow', render: (r) => (
              <div>
                <div className="font-semibold text-v2-ink">{r.name}</div>
                <div className="text-[10.5px] text-v2-ink-muted">via <span className="font-mono-v2">{r.agent}</span></div>
              </div>
            )},
            { key: 'nodeCount',  header: 'Nodes', align: 'right', render: (r) => <span className="font-semibold tabular-nums">{r.nodeCount}</span> },
            { key: 'complexity', header: 'Complexity', render: (r) => <Pill tone={complexityTone(r.complexity)} size="sm">{r.complexity}</Pill> },
            { key: 'lastRunAt',  header: 'Last run', render: (r) => <span className="text-[12px] text-v2-ink-secondary">{r.lastRunAt ? new Date(r.lastRunAt).toLocaleString() : '—'}</span> },
            { key: 'status',     header: 'Status', render: (r) => <Pill tone={r.status === 'unregistered' ? 'warning' : r.status === 'pending_review' ? 'cyan' : 'muted'} dot size="sm">{r.status}</Pill> },
            { key: 'actions',    header: '', align: 'right', render: (r) => (
              <Button variant="primary" size="sm" iconRight={<ArrowRight />} onClick={(e) => { e.stopPropagation(); registerOne(r); }}>
                Register
              </Button>
            )},
          ]}
          rows={filtered}
          rowKey={(r) => r.id}
          onRowClick={setSelected}
        />
      )}

      <Slideout
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        ariaLabel={selected ? `${selected.name} workflow detail` : 'Workflow detail'}
        width={520}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={selected.status === 'unregistered' ? 'warning' : 'cyan'} dot size="sm">{selected.status}</Pill>
              <Pill tone={complexityTone(selected.complexity)} size="sm">{selected.complexity} complexity</Pill>
              <Pill tone="muted" size="sm">{selected.nodeCount} nodes</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0 mb-1">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">via {selected.agent}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<FileText />} onClick={() => registerOne(selected)}>Register workflow</Button>
              <Button variant="secondary" size="sm">Mark as ad-hoc</Button>
            </div>
          </div>
        )}
      >
        {selected && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            <Card padding="sm">
              <Card.Header title="Workflow details" />
              <Card.Body>
                <dl className="grid grid-cols-[120px_1fr] gap-y-1.5 gap-x-3 text-[12px]">
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Owning agent</dt>
                  <dd className="text-v2-ink font-mono-v2">{selected.agent}</dd>
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Last run</dt>
                  <dd className="text-v2-ink">{selected.lastRunAt ? new Date(selected.lastRunAt).toLocaleString() : '—'}</dd>
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Node count</dt>
                  <dd className="text-v2-ink tabular-nums">{selected.nodeCount}</dd>
                </dl>
              </Card.Body>
            </Card>
            <p>
              Registering brings this workflow under protection. The original
              graph is captured as the baseline; future modifications go through
              the standard publish gate.
            </p>
          </div>
        )}
      </Slideout>
    </div>
  );
}

/* G112 — proper v2 inventory pattern (FacetRail + Table + Slideout). */
import ResolvedWorkflowDiscoveryPageV2 from './WorkflowDiscoveryPageV2';
export default function ResolvedDiscoveryWorkflowsPage(props) {
  return <ResolvedWorkflowDiscoveryPageV2 {...props} />;
}
export { DiscoveryWorkflowsPageV2Inner };
